import { useCounterpartyFilter } from '@src/hooks/filters/useCounterpartyFilter';
import { useIDFilter } from '@src/hooks/filters/useIDFilter';
import { useMarketAccountsFilter } from '@src/hooks/filters/useMarketAccountsFilter';
import { useModeFilter } from '@src/hooks/filters/useModeFilter';
import { usePricingAggregationsFilter } from '@src/hooks/filters/usePricingAggregationsFilter';
import { usePricingTierFilters } from '@src/hooks/filters/usePricingTierFilters';
import { useSidesFilter } from '@src/hooks/filters/useSidesFilter';
import { useSubAccountsFilter } from '@src/hooks/filters/useSubAccountsFilter';
import { useUsersFilter } from '@src/hooks/filters/useUsersFilter';
import {
  IconName,
  PricingModeEnum,
  useBasePairFilter,
  useCurrenciesContext,
  useCurrenciesFilter,
  useFeeCurrenciesFilter,
  useMarketFeeModesFilter,
  useSymbolsFilter,
  type FilterableProperty,
} from '@talos/kyoko';
import { startCase, values } from 'lodash-es';
import { useMemo } from 'react';
import { DealerSpaceParameterKey, type DealerFilterBuilderSpaceParameterKey } from './types';

export function useDealerSpaceParameterProperties() {
  const counterpartyFilter = useCounterpartyFilter({ label: 'Customer' });
  const symbolsFilter = useSymbolsFilter();
  const currencyFilter = useCurrenciesFilter();
  const basePairFilter = useBasePairFilter();
  const marketAccountsFilter = useMarketAccountsFilter({ label: 'Customer Market Account' });
  const sidesFilter = useSidesFilter();
  const usersFilter = useUsersFilter();
  const subAccountsFilter = useSubAccountsFilter({});
  const modeFilter = useModeFilter();
  const idFilter = useIDFilter();
  const { allInTierFilter, feeTierFilter, symbolGroupFilter } = usePricingTierFilters({
    preserveSelectionsWhileLoading: true,
  });
  const feeModesFilter = useMarketFeeModesFilter();
  const { currenciesList } = useCurrenciesContext();
  const feeCurrencies = useMemo(() => currenciesList.map(currency => currency.Symbol), [currenciesList]);
  const feeCurrenciesFilter = useFeeCurrenciesFilter(feeCurrencies);
  const pricingAggregationsFilter = usePricingAggregationsFilter();

  return useMemo(() => {
    const entries: [DealerFilterBuilderSpaceParameterKey, FilterableProperty][] = [
      [DealerSpaceParameterKey.Counterparty, counterpartyFilter],
      [DealerSpaceParameterKey.Symbols, symbolsFilter],
      [DealerSpaceParameterKey.Currencies, currencyFilter],
      [DealerSpaceParameterKey.BasePairs, basePairFilter],
      [DealerSpaceParameterKey.MarketAccounts, marketAccountsFilter],
      [DealerSpaceParameterKey.Sides, sidesFilter],
      [DealerSpaceParameterKey.Users, usersFilter],
      [
        DealerSpaceParameterKey.PricingMode,
        {
          key: DealerSpaceParameterKey.PricingMode,
          label: 'Pricing Mode',
          icon: IconName.CurrencyDollar,
          options: values(PricingModeEnum),
          getOptionLabel: startCase,
        },
      ],
      [DealerSpaceParameterKey.SubAccounts, subAccountsFilter],
      [DealerSpaceParameterKey.OrderID, { ...idFilter, key: DealerSpaceParameterKey.OrderID, label: 'Order ID' }],
      [DealerSpaceParameterKey.RFQID, { ...idFilter, key: DealerSpaceParameterKey.RFQID, label: 'RFQ ID' }],
      [DealerSpaceParameterKey.Modes, modeFilter],
      [DealerSpaceParameterKey.FeeModes, feeModesFilter],
      [DealerSpaceParameterKey.FeeCurrencies, feeCurrenciesFilter],
      [DealerSpaceParameterKey.PricingAggregations, pricingAggregationsFilter],
      [DealerSpaceParameterKey.AllInTiers, allInTierFilter],
      [DealerSpaceParameterKey.FeeTiers, feeTierFilter],
      [DealerSpaceParameterKey.SymbolGroups, symbolGroupFilter],
    ];
    return new Map(entries);
  }, [
    allInTierFilter,
    basePairFilter,
    counterpartyFilter,
    currencyFilter,
    feeCurrenciesFilter,
    feeModesFilter,
    feeTierFilter,
    idFilter,
    marketAccountsFilter,
    modeFilter,
    pricingAggregationsFilter,
    sidesFilter,
    subAccountsFilter,
    symbolGroupFilter,
    symbolsFilter,
    usersFilter,
  ]);
}
